// The GATE LIST — the "Needs you" list (Today, prototype .gate rows) and the Approvals inbox (prototype
// .appr rows) share this one delivery path for the exercise (AR-MOD-4). It renders each pending Gate the
// projection carries and, on the bound-owner ones, an exercise control that ROUTES to the owning module's
// Gate (it sets no approval field — P-GATE). The design rule the brief fixes (§4): ONLY the FIRST bound
// pending gate carries the primary (Forest) button; every later one is ghost — one primary action per view
// (design brief §3). A gate not bound to the authenticated human shows no exercise control (G-180).
//
// Tokens only (G-181); the item derives its label/why from the owning projection (no internal id, G-197).

import { useState } from 'react';
import type { ApprovalItem, ApprovalGateKind } from '../projections.ts';
import type { RouteResult } from '../api-client.ts';
import { EmptyState } from '../primitives.tsx';
import { APPROVAL_GATE_LABELS } from '../approval-gate-labels.ts';
import { isApprovalReadyForViewer } from '../approval-triage.ts';
import { GateRow, type GateRowVariant } from './GateRow.tsx';

export interface GateListProps {
  readonly gates: readonly ApprovalItem[];
  readonly variant: GateRowVariant;
  readonly emptyText?: string;
  /** Route a gate exercise to the owning module's Gate; returns the honest routed/refused result. */
  readonly onExercise: (
    kind: ApprovalGateKind,
    objectId: string,
    expectedRevision: number | null,
  ) => Promise<RouteResult>;
  /** Route a gate DECLINE with the mandatory reason (Approvals surface only — BUILD-DEC-116). */
  readonly onDecline?: (
    kind: ApprovalGateKind,
    objectId: string,
    reason: string,
    expectedRevision?: number,
  ) => Promise<RouteResult>;
  /** Schedule-only review response; absent on Today and on builds without the owning route. */
  readonly onRequestChanges?: (
    scheduleId: string,
    reason: string,
    expectedRevision?: number,
  ) => Promise<RouteResult>;
}

/** Preserve one honest refusal-note path for every routed Gate action. */
async function routeWithRefusalNote(
  route: () => Promise<RouteResult>,
  reportRefusal: (note: string) => void,
): Promise<RouteResult> {
  const result = await route();
  if (!result.ok) reportRefusal(result.note ?? 'That action was not permitted.');
  return result;
}

function GateRefusalNote({ note }: { readonly note: string | null }): React.JSX.Element | null {
  if (note === null) {
    return null;
  }
  return (
    <p role="status" className="gate-note">
      {note}
    </p>
  );
}

/** Render the pending-gate list; only the first bound gate is primary (brief §4). */
export function GateList({
  gates,
  variant,
  onExercise,
  onDecline,
  onRequestChanges,
  emptyText = 'Nothing needs you right now.',
}: GateListProps): React.JSX.Element {
  const [note, setNote] = useState<string | null>(null);

  if (gates.length === 0) {
    return <EmptyState>{emptyText}</EmptyState>;
  }

  // Surface a routed refusal under the list (one honest note path for approve + decline — F6 family).
  const noting = (route: () => Promise<RouteResult>) => async (): Promise<RouteResult> => {
    return routeWithRefusalNote(route, setNote);
  };

  // The FIRST bound gate carries the primary button (design rule); every later one is ghost.
  const firstBound = gates.findIndex(isApprovalReadyForViewer);
  return (
    <div className={variant === 'gate' ? 'gate-list' : 'appr-list'}>
      {gates.map((gate, index) => (
        <GateRow
          key={`${gate.kind}:${gate.objectId}`}
          gate={gate}
          variant={variant}
          kindLabel={APPROVAL_GATE_LABELS[gate.kind]}
          primary={index === firstBound}
          onExercise={noting(() => onExercise(gate.kind, gate.objectId, gate.reviewRevision))}
          {...(onDecline !== undefined
            ? {
                onDecline: (reason: string) =>
                  noting(() =>
                    onDecline(gate.kind, gate.objectId, reason, gate.reviewRevision ?? undefined),
                  )(),
              }
            : {})}
          {...(gate.kind === 'schedule_activation' && onRequestChanges !== undefined
            ? {
                onRequestChanges: (reason: string) =>
                  noting(() =>
                    onRequestChanges(gate.objectId, reason, gate.reviewRevision ?? undefined),
                  )(),
              }
            : {})}
        />
      ))}
      <GateRefusalNote note={note} />
    </div>
  );
}
